import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Plus, Trash2, Loader2, Download, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import HistorySection from '../history/HistorySection';

// ── Types ────────────────────────────────────────────────────────────────────

interface PerformanceReport {
  score: number;
  fcp: number; lcp: number; tbt: number; cls: number; tti: number; speedIndex: number;
}
interface SEOReport {
  score: number;
  title: { present: boolean; content: string | null };
  metaDescription: { present: boolean; content: string | null };
  crawlable: boolean;
  structuredData: { present: boolean; types: string[] };
  mobileViewport: boolean;
  canonicalUrl: { present: boolean; href: string | null };
}
interface SocialLink { platform: string; url: string; }
interface ScraperReport { techStack: string[]; analytics: string[]; paidMedia: string[]; socialLinks: SocialLink[]; }

type AuditResult =
  | { status: 'success'; url: string; name: string; performance: PerformanceReport; seo: SEOReport; scraper: ScraperReport; elapsedMs: number }
  | { status: 'failure'; url: string; name: string; error: string; elapsedMs: number };

// ── Helpers ──────────────────────────────────────────────────────────────────

const THRESHOLDS = {
  fcp: 1800, lcp: 2500, tbt: 200, cls: 0.1, tti: 3800, speedIndex: 3400,
};
const METRIC_LABELS: Record<string, string> = {
  fcp: 'First Contentful Paint', lcp: 'Largest Contentful Paint',
  tbt: 'Total Blocking Time', cls: 'Cumulative Layout Shift',
  tti: 'Time to Interactive', speedIndex: 'Speed Index',
};
const METRIC_TARGETS: Record<string, string> = {
  fcp: '< 1.8 s', lcp: '< 2.5 s', tbt: '< 200 ms', cls: '< 0.1', tti: '< 3.8 s', speedIndex: '< 3.4 s',
};

function fmtMs(v: number, key: string) {
  if (key === 'cls') return v.toFixed(3);
  return v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${v} ms`;
}
function passes(key: string, v: number) { return v <= (THRESHOLDS as any)[key]; }

function scoreColor(s: number) {
  if (s >= 90) return 'text-[#54c4b6]';
  if (s >= 50) return 'text-[#e4c184]';
  return 'text-[#216974]';
}
function scoreBg(s: number) {
  if (s >= 90) return 'bg-[#54c4b6] border-[#54c4b6]';
  if (s >= 50) return 'bg-[#e4c184] border-[#e4c184]';
  return 'bg-[#216974] border-red-400';
}

const SOCIAL_COLORS: Record<string, string> = {
  Instagram: '#E1306C', Facebook: '#1877F2', 'Twitter / X': '#000',
  LinkedIn: '#0A66C2', YouTube: '#FF0000', WhatsApp: '#25D366',
  TikTok: '#010101', Pinterest: '#E60023', Telegram: '#26A5E4',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function ScoreCard({ label, score }: { label: string; score: number }) {
  return (
    <div className={`border-2 rounded-xl p-4 text-center min-w-[80px] ${scoreBg(score)}`}>
      <div className={`text-3xl font-black leading-none ${scoreColor(score)}`}>{score}</div>
      <div className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${scoreColor(score)}`}>{label}</div>
      <div className="text-[9px] text-gray-400 mt-0.5">/ 100</div>
    </div>
  );
}

function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span className="px-2 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap border" style={{ color, background: bg, borderColor: color + '30' }}>
      {label}
    </span>
  );
}

function ResultCard({ result }: { result: AuditResult }) {
  const [open, setOpen] = useState(true);

  if (result.status === 'failure') {
    return (
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="flex items-center gap-3 p-5 bg-[#216974] border-l-4 border-red-500">
          <XCircle size={20} className="text-[#216974] shrink-0" />
          <div>
            <p className="font-bold text-gray-900">{result.name}</p>
            <p className="text-xs text-gray-500">{result.url}</p>
            <p className="text-xs text-[#216974] mt-1">{result.error}</p>
          </div>
        </div>
      </Card>
    );
  }

  const { performance: p, seo: s, scraper: sc } = result;

  return (
    <Card className="border-0 shadow-lg overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between p-5 bg-slate-900 text-white text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-4">
          <CheckCircle size={18} className="text-[#54c4b6] shrink-0" />
          <div>
            <p className="font-bold text-base leading-tight">{result.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">{result.url} · {(result.elapsedMs / 1000).toFixed(1)}s</p>
          </div>
          {/* Mini score badges in header */}
          <div className="flex gap-2 ml-4">
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${p.score >= 90 ? 'bg-[#54c4b6]' : p.score >= 50 ? 'bg-[#e4c184]' : 'bg-[#216974]'} text-white`}>
              Perf {p.score}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.score >= 90 ? 'bg-[#54c4b6]' : s.score >= 50 ? 'bg-[#e4c184]' : 'bg-[#216974]'} text-white`}>
              SEO {s.score}
            </span>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            {/* Score row */}
            <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-100">
              <ScoreCard label="Performance" score={p.score} />
              <ScoreCard label="SEO" score={s.score} />
              <div className="ml-2 text-xs text-gray-500 space-y-1">
                <div><span className="text-[#54c4b6] font-bold">●</span> 90–100 Good</div>
                <div><span className="text-[#e4c184] font-bold">●</span> 50–89 Needs work</div>
                <div><span className="text-[#216974] font-bold">●</span> 0–49 Poor</div>
              </div>
            </div>

            {/* Two data columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
              {/* Performance metrics */}
              <div className="p-5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-3 pb-2 border-b border-gray-100">Performance Metrics</p>
                <table className="w-full text-xs">
                  <tbody>
                    {(['fcp','lcp','tbt','cls','tti','speedIndex'] as const).map(key => {
                      const val = p[key] as number;
                      const ok = passes(key, val);
                      return (
                        <tr key={key} className="border-b border-gray-50">
                          <td className="py-1.5 text-gray-500 pr-3">{METRIC_LABELS[key]}</td>
                          <td className="py-1.5 font-bold text-gray-900 pr-3">{fmtMs(val, key)}</td>
                          <td className="py-1.5 text-gray-400 pr-3">{METRIC_TARGETS[key]}</td>
                          <td className="py-1.5 text-right">{ok
                            ? <span className="text-[#54c4b6] font-bold">●</span>
                            : <span className="text-[#216974] font-bold">●</span>
                          }</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* SEO details */}
              <div className="p-5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-3 pb-2 border-b border-gray-100">SEO Details</p>
                <table className="w-full text-xs">
                  <tbody>
                    {[
                      ['Page Title', s.title.content ?? '(not set)', null],
                      ['Meta Description', s.metaDescription.present ? 'Present' : 'Not found', s.metaDescription.present],
                      ['Canonical URL', s.canonicalUrl.href ?? '(not set)', s.canonicalUrl.present],
                      ['Crawlable', s.crawlable ? 'Yes — indexable' : 'No — noindex', s.crawlable],
                      ['Mobile Viewport', s.mobileViewport ? 'Configured' : 'Missing', s.mobileViewport],
                      ['Structured Data', s.structuredData.types.length ? s.structuredData.types.join(', ') : s.structuredData.present ? 'Present' : 'Not detected', s.structuredData.present],
                    ].map(([label, value, ok]) => (
                      <tr key={label as string} className="border-b border-gray-50">
                        <td className="py-1.5 text-gray-500 pr-2 whitespace-nowrap">{label as string}</td>
                        <td className="py-1.5 text-gray-800 text-[10px] break-all pr-2">{value as string}</td>
                        <td className="py-1.5 text-right">
                          {ok === null ? '' : (ok as boolean)
                            ? <span className="text-[#54c4b6] font-bold">●</span>
                            : <span className="text-[#216974] font-bold">●</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tech / Analytics / Paid / Social */}
            <div className="px-5 py-4 space-y-3 border-t border-gray-100">
              {[
                { label: 'Tech Stack', items: sc.techStack, color: '#216974', bg: '#eff6ff', empty: 'Not detected' },
                { label: 'Analytics & Tracking', items: sc.analytics, color: '#216974', bg: '#f5f3ff', empty: 'None detected' },
                { label: 'Paid Media Signals', items: sc.paidMedia, color: '#b45309', bg: '#fffbeb', empty: 'No paid signals' },
              ].map(row => (
                <div key={row.label} className="flex items-start gap-3">
                  <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 whitespace-nowrap pt-0.5 min-w-[130px]">{row.label}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {row.items.length > 0
                      ? row.items.map(i => <Badge key={i} label={i} color={row.color} bg={row.bg} />)
                      : <Badge label={row.empty} color="#94a3b8" bg="#f8fafc" />
                    }
                  </div>
                </div>
              ))}
              {/* Social pills */}
              <div className="flex items-start gap-3">
                <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 whitespace-nowrap pt-0.5 min-w-[130px]">Social Media</span>
                <div className="flex flex-wrap gap-2">
                  {sc.socialLinks.length > 0
                    ? sc.socialLinks.map(l => (
                        <a
                          key={l.platform}
                          href={l.url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1 rounded-full text-[10px] font-bold text-white whitespace-nowrap"
                          style={{ background: SOCIAL_COLORS[l.platform] || '#64748b' }}
                        >
                          {l.platform}
                        </a>
                      ))
                    : <span className="text-xs text-gray-400">No social links found</span>
                  }
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function WebsiteReport() {
  const [urls, setUrls] = useState<string[]>(['']);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<AuditResult[]>([]);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabSwitched, setTabSwitched] = useState(false);

  // Restore running state on mount
  useEffect(() => {
    const savedState = localStorage.getItem('audit-running');
    if (savedState) {
      const { timestamp, urls: savedUrls } = JSON.parse(savedState);
      // If audit was started less than 5 minutes ago, show as running
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        setRunning(true);
        setUrls(savedUrls);
        setError('Previous audit may still be running on server. Results will appear when complete.');
      } else {
        localStorage.removeItem('audit-running');
      }
    }
  }, []);

  // Detect when user switches tabs during audit
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && running) {
        setTabSwitched(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [running]);

  const addUrl = () => setUrls(u => [...u, '']);
  const removeUrl = (i: number) => setUrls(u => u.filter((_, idx) => idx !== i));
  const updateUrl = (i: number, val: string) => setUrls(u => u.map((x, idx) => idx === i ? val : x));

  const handleRun = async () => {
    const valid = urls.map(u => u.trim()).filter(Boolean);
    if (valid.length === 0) { setError('Add at least one URL'); return; }
    if (valid.length > 10) { setError('Maximum 10 URLs at a time'); return; }

    setRunning(true);
    setError(null);
    setResults([]);
    setPdfBase64(null);
    setTabSwitched(false);

    // Save state to localStorage so we can restore if user navigates away
    localStorage.setItem('audit-running', JSON.stringify({ 
      timestamp: Date.now(), 
      urls: valid 
    }));
    
    try {
      const res = await fetch('/api/website-audit/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls: valid }),
        // Keep connection alive even when navigating away
        keepalive: true,
      });
      
      // Clear saved state on completion
      localStorage.removeItem('audit-running');
      
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Audit failed');
      }
      const data = await res.json();
      setResults(data.results || []);
      setPdfBase64(data.pdfBase64 || null);
    } catch (err: any) {
      localStorage.removeItem('audit-running');
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const downloadPDF = () => {
    if (!pdfBase64) return;
    const blob = new Blob([Uint8Array.from(atob(pdfBase64), c => c.charCodeAt(0))], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `website-audit-${new Date().toISOString().slice(0, 10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const failCount = results.filter(r => r.status === 'failure').length;

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex p-3 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl shadow-lg shadow-slate-200">
          <Globe size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">Website Report</h1>
        <p className="text-gray-500 text-lg max-w-lg mx-auto font-medium">
          Performance & SEO audit powered by Lighthouse — detects tech stack, analytics, paid media & social presence. Exports as PDF.
        </p>
      </div>

      {/* URL input card */}
      <Card className="border-0 shadow-2xl shadow-slate-100">
        <div className="p-8 space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-bold text-gray-700">Websites to Audit <span className="font-normal text-gray-400">(max 10)</span></label>
            <span className="text-xs text-gray-400">{urls.filter(u => u.trim()).length} / 10</span>
          </div>

          <div className="space-y-2">
            {urls.map((url, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  value={url}
                  onChange={e => updateUrl(i, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addUrl(); }}
                  placeholder={i === 0 ? 'https://example.com' : 'https://another-site.com'}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                />
                {urls.length > 1 && (
                  <button
                    onClick={() => removeUrl(i)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#216974] hover:bg-[#216974]/10 text-[#216974] transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {urls.length < 10 && (
            <button
              onClick={addUrl}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 font-semibold transition-colors"
            >
              <Plus size={15} /> Add another URL
            </button>
          )}

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-semibold"
              >
                ⚠️ {error}
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            onClick={handleRun}
            isLoading={running}
            className="w-full h-12 bg-gradient-to-r from-slate-800 to-slate-900 hover:opacity-90 transition-opacity text-white font-bold text-base rounded-xl flex items-center justify-center gap-2"
            leftIcon={running ? <Loader2 className="animate-spin" size={20} /> : <Globe size={20} />}
          >
            {running
              ? `Auditing ${urls.filter(u => u.trim()).length} site${urls.filter(u => u.trim()).length > 1 ? 's' : ''}… (this may take a minute)`
              : `Run Audit`}
          </Button>

          {running && (
            <div className="space-y-2">
              <p className="text-center text-xs text-gray-400">
                Each site takes ~15–25 seconds — Running 3 sites in parallel
              </p>
              <p className="text-center text-xs font-semibold text-[#54c4b6]">
                ✓ You can navigate to other tools — audit continues on server
              </p>
              {tabSwitched && (
                <p className="text-center text-xs font-bold text-amber-600">
                  💡 Come back to this page to see results when complete
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Results */}
      <AnimatePresence>
        {results.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="space-y-4">
            {/* Summary bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Results</h2>
                <div className="flex gap-2">
                  {successCount > 0 && (
                    <span className="px-2.5 py-1 bg-[#54c4b6]/10 text-[#216974] rounded-full text-xs font-bold">
                      ✓ {successCount} passed
                    </span>
                  )}
                  {failCount > 0 && (
                    <span className="px-2.5 py-1 bg-[#216974]/10 text-[#216974] rounded-full text-xs font-bold">
                      ✗ {failCount} failed
                    </span>
                  )}
                </div>
              </div>
              {pdfBase64 && (
                <Button
                  onClick={downloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-semibold text-sm transition-colors"
                  leftIcon={<Download size={15} />}
                >
                  Download PDF Report
                </Button>
              )}
            </div>

            {results.map((r, i) => (
              <motion.div key={r.url + i} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                <ResultCard result={r} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <HistorySection toolId="website-report" />
    </div>
  );
}
